import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { api, getApiError } from "@/lib/api";
import { fmtLog } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PolicyContent } from "@/components/PolicyEditor";
import {
  Sparkles, ChevronDown, ChevronRight, AlertTriangle, RefreshCw, Loader2, X, ExternalLink, MessageSquare,
  Wand2, Send, Check, EyeOff, Undo2, Lock,
} from "lucide-react";
import { DOCS_ACCENT as ACCENT } from "@/lib/govDocs";

const NDIS = "#0f766e";
const SEVERITY = {
  high: "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
};
const CATEGORY = { gap: "Gap", clarity: "Clarity", consistency: "Consistency", conflict: "Conflict", compliance: "Compliance" };
const OP_LABEL = { replace: "Change", insert_after: "Add after", insert_before: "Add before", delete: "Remove" };
const chip = "inline-flex items-center justify-center min-w-[1.25rem] h-[1.05rem] px-1 mx-0.5 rounded text-[10px] font-semibold text-white align-text-top";
const fmtUpdated = (d) => (d ? d.split("-").reverse().join("/") : "");

// Plain text from the AI: **bold**, [S#] / [C#] citations and "- " bullets (no HTML from the model is rendered).
function AiText({ text, sources = [], ndisSources = [] }) {
  const byS = Object.fromEntries(sources.map((s) => [s.n, s]));
  const byC = Object.fromEntries(ndisSources.map((s) => [s.n, s]));
  const inline = (t) => String(t).split(/(\*\*[^*]+\*\*|\[[SC]\d{1,2}\])/g).map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    const c = p.match(/^\[C(\d{1,2})\]$/);
    if (c) {
      const s = byC[Number(c[1])];
      return s ? <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" title={`NDIS Commission: ${s.label}`} className={chip} style={{ backgroundColor: NDIS }}>C{c[1]}</a> : null;
    }
    const m = p.match(/^\[S(\d{1,2})\]$/);
    if (m) {
      const s = byS[Number(m[1])];
      return s ? <Link key={i} to={`/documents/${s.library}/${s.document_id}`} title={s.label} className={chip} style={{ backgroundColor: ACCENT }}>{m[1]}</Link> : null;
    }
    return <Fragment key={i}>{p}</Fragment>;
  });
  const blocks = [];
  let list = null;
  String(text || "").split("\n").forEach((line) => {
    const t = line.trim();
    if (/^[-*•]\s+/.test(t)) { if (!list) { list = []; blocks.push({ list }); } list.push(t.replace(/^[-*•]\s+/, "")); }
    else { list = null; if (t) blocks.push({ p: t }); }
  });
  return (
    <div className="space-y-1.5">
      {blocks.map((b, i) => (b.list
        ? <ul key={i} className="list-disc pl-4 space-y-0.5">{b.list.map((li, j) => <li key={j}>{inline(li)}</li>)}</ul>
        : <p key={i}>{inline(b.p)}</p>))}
    </div>
  );
}

function Discussion({ sg, busy, onSend }) {
  const [msg, setMsg] = useState("");
  const send = async () => { if (msg.trim() && (await onSend(msg.trim()))) setMsg(""); };
  return (
    <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid="suggestion-thread">
      {sg.thread.length === 0 && (
        <div className="text-[11px] text-slate-500 mb-2">Ask the AI about this suggestion: why it matters, whether it applies to us, or for exact wording.</div>
      )}
      <div className="space-y-2 mb-2">
        {sg.thread.map((m, i) => (m.role === "assistant" ? (
          <div key={i} className="text-xs text-slate-700 bg-white border border-slate-200 rounded-lg px-2.5 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide mb-1 flex items-center gap-1" style={{ color: ACCENT }}><Sparkles className="h-3 w-3" />AI</div>
            <AiText text={m.text} sources={m.sources} ndisSources={m.ndis_sources} />
          </div>
        ) : (
          <div key={i} className="text-xs text-slate-800 ml-8 rounded-lg px-2.5 py-2" style={{ backgroundColor: `${ACCENT}14` }}>
            <div className="text-[10px] text-slate-500 mb-0.5">{m.by_name} · {fmtLog(m.at)}</div>
            <div className="whitespace-pre-line">{m.text}</div>
          </div>
        )))}
      </div>
      <div className="flex gap-2 items-end">
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} maxLength={2000} className="text-xs bg-white"
          placeholder="e.g. Does this apply to a provider our size?" data-testid="suggestion-chat-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button size="sm" className="h-8" style={{ backgroundColor: ACCENT }} onClick={send} disabled={busy || !msg.trim()} data-testid="suggestion-chat-send">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function Proposal({ p, busy, locked, onApply, onRevise, onCancel }) {
  const [changes, setChanges] = useState("");
  return (
    <div className="mt-2 rounded-lg border p-2.5" style={{ borderColor: `${ACCENT}55` }} data-testid="suggestion-proposal">
      <div className="text-xs font-semibold text-slate-700 flex items-center gap-1"><Wand2 className="h-3.5 w-3.5" style={{ color: ACCENT }} />Proposed change</div>
      {p.summary && <div className="text-xs text-slate-700 mt-1">{p.summary}</div>}
      {p.note && <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5">{p.note}</div>}
      {p.out_of_date && (
        <div className="text-[11px] text-rose-700 mt-1.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />The draft has changed since this was proposed. Ask again so it works from the latest text.</div>
      )}
      <div className="space-y-2 mt-2">
        {p.preview.map((it, i) => (
          <div key={i} className="text-xs">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{OP_LABEL[it.op]} paragraph {it.block}</div>
            {it.context && <div className="opacity-60 border-l-2 border-slate-300 pl-2 mb-1 max-h-24 overflow-hidden"><PolicyContent html={it.context} /></div>}
            {it.before && <div className="border-l-2 border-rose-400 bg-rose-50/60 pl-2 py-0.5 line-through decoration-rose-300"><PolicyContent html={it.before} /></div>}
            {it.after && <div className="border-l-2 border-emerald-500 bg-emerald-50/60 pl-2 py-0.5 mt-1"><PolicyContent html={it.after} /></div>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2.5">
        <Button size="sm" className="h-7 text-xs" style={{ backgroundColor: ACCENT }} onClick={onApply} disabled={busy || locked || p.out_of_date} data-testid="suggestion-apply">
          {busy === "apply" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}Apply to draft
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onCancel} disabled={!!busy}>Cancel</Button>
      </div>
      <div className="flex gap-2 items-end mt-2">
        <Textarea value={changes} onChange={(e) => setChanges(e.target.value)} rows={1} maxLength={2000} className="text-xs"
          placeholder={p.out_of_date ? "Ask again (optionally say what to change)" : "Not quite right? Say what to change, e.g. make it shorter"} data-testid="suggestion-revise-input" />
        <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap" disabled={!!busy || locked}
          onClick={() => onRevise(changes.trim() || p.instructions || "")} data-testid="suggestion-revise">
          {busy === "propose" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}Ask again
        </Button>
      </div>
    </div>
  );
}

function SuggestionCard({ sg, n, review, docId, locked, beforeEdit, onReview, onDraftChanged }) {
  const [open, setOpen] = useState(null);            // "discuss" | "dismiss" | null
  const [busy, setBusy] = useState("");              // "chat" | "propose" | "apply" | "dismiss" ...
  const [reason, setReason] = useState("");
  const bySource = Object.fromEntries((review.sources || []).map((s) => [`S${s.n}`, s]));
  const byNdis = Object.fromEntries((review.ndis_sources || []).map((s) => [`C${s.n}`, s]));
  const related = String(sg.source || "").match(/S\d{1,2}/g)?.map((k) => bySource[k]).filter(Boolean) || [];
  const guidance = String(sg.source || "").match(/C\d{1,2}/g)?.map((k) => byNdis[k]).filter(Boolean) || [];

  const act = async (name, fn, fail) => {
    setBusy(name);
    try { onReview(await fn()); return true; }
    catch (e) { toast.error(getApiError(e, fail)); return false; }
    finally { setBusy(""); }
  };
  const propose = (instructions) => act("propose", async () => {
    if (!(await beforeEdit())) throw new Error("Save the draft first.");
    return api.govSuggestionPropose(docId, sg.id, instructions);
  }, "The AI couldn't propose a change this time.");
  const apply = () => act("apply", async () => {
    if (!(await beforeEdit())) throw new Error("Save the draft first.");
    const r = await api.govSuggestionApply(docId, sg.id, sg.proposal.at);
    toast.success("Change applied to the draft");
    await onDraftChanged();
    return r;
  }, "The change couldn't be applied.");

  const muted = sg.status !== "open";
  return (
    <li className={`rounded-lg border px-3 py-2.5 ${muted ? "border-slate-100 bg-slate-50/60" : "border-slate-200"}`} data-testid={`suggestion-${n}`}>
      <div className="flex flex-wrap items-center gap-1.5 mb-1">
        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${SEVERITY[sg.severity]}`}>{sg.severity}</span>
        <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border border-slate-200 text-slate-500">{CATEGORY[sg.category] || sg.category}</span>
        {sg.section && <span className="text-[11px] text-slate-500">in “{sg.section}”</span>}
        {sg.status === "applied" && <span className="ml-auto text-[10px] font-semibold uppercase text-emerald-700 flex items-center gap-1"><Check className="h-3 w-3" />Applied</span>}
        {sg.status === "dismissed" && <span className="ml-auto text-[10px] font-semibold uppercase text-slate-400">Dismissed</span>}
      </div>
      <div className={`text-sm ${muted ? "text-slate-500" : "text-slate-800"}`}>{sg.issue}</div>
      {sg.suggestion && sg.status !== "applied" && <div className="text-sm text-slate-600 mt-1"><span className="font-medium text-slate-700">Suggestion: </span>{sg.suggestion}</div>}
      {sg.status === "applied" && sg.applied && (
        <div className="text-[11px] text-emerald-800 mt-1">{sg.applied.summary} <span className="text-slate-400">· {sg.applied.by_name}, {fmtLog(sg.applied.at)}</span></div>
      )}
      {sg.status === "dismissed" && sg.dismissed && (
        <div className="text-[11px] text-slate-500 mt-1">Dismissed by {sg.dismissed.by_name}, {fmtLog(sg.dismissed.at)}{sg.dismissed.reason ? `: “${sg.dismissed.reason}”` : ""}</div>
      )}
      {!muted && related.map((r) => (
        <Link key={r.n} to={`/documents/${r.library}/${r.document_id}`} className="inline-block text-[11px] mt-1 mr-3 hover:underline" style={{ color: ACCENT }}>
          See {r.label}{r.heading ? ` › ${r.heading}` : ""}
        </Link>
      ))}
      {!muted && guidance.map((g) => (
        <a key={`c${g.n}`} href={g.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] mt-1 mr-3 text-[#0f766e] hover:underline" data-testid="ai-review-ndis-link">
          NDIS Commission guidance: {g.label}{g.heading ? ` › ${g.heading}` : ""}{g.updated ? ` (updated ${fmtUpdated(g.updated)})` : ""}
          <ExternalLink className="h-3 w-3" />
        </a>
      ))}

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {sg.status === "open" && (
          <>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(open === "discuss" ? null : "discuss")} data-testid="suggestion-discuss">
              <MessageSquare className="h-3 w-3 mr-1" />Discuss{sg.thread.length ? ` (${sg.thread.filter((m) => m.role === "user").length})` : ""}
            </Button>
            {!sg.proposal && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => propose("")} disabled={!!busy || locked}
                title={locked ? "Withdraw the draft from approval to make changes" : undefined} data-testid="suggestion-make-change">
                {busy === "propose" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Wand2 className="h-3 w-3 mr-1" />}Make this change
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => setOpen(open === "dismiss" ? null : "dismiss")} data-testid="suggestion-dismiss">
              <EyeOff className="h-3 w-3 mr-1" />Dismiss
            </Button>
          </>
        )}
        {sg.status === "dismissed" && (
          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" disabled={!!busy}
            onClick={() => act("restore", () => api.govSuggestionRestore(docId, sg.id), "Couldn't restore it.")} data-testid="suggestion-restore">
            <Undo2 className="h-3 w-3 mr-1" />Restore
          </Button>
        )}
        {sg.status !== "open" && sg.thread.length > 0 && (
          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => setOpen(open === "discuss" ? null : "discuss")}>
            <MessageSquare className="h-3 w-3 mr-1" />Discussion ({sg.thread.filter((m) => m.role === "user").length})
          </Button>
        )}
        {locked && sg.status === "open" && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Lock className="h-3 w-3" />Withdraw the draft from approval to make changes</span>}
      </div>

      {open === "dismiss" && sg.status === "open" && (
        <div className="flex gap-2 items-center mt-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="Why? (optional)"
            className="flex-1 h-8 text-xs border border-slate-200 rounded-md px-2" data-testid="suggestion-dismiss-reason" />
          <Button size="sm" className="h-8 text-xs" variant="outline" disabled={!!busy} data-testid="suggestion-dismiss-confirm"
            onClick={async () => { if (await act("dismiss", () => api.govSuggestionDismiss(docId, sg.id, reason.trim()), "Couldn't dismiss it.")) { setOpen(null); setReason(""); } }}>
            Dismiss suggestion
          </Button>
        </div>
      )}
      {open === "discuss" && (
        sg.status === "open"
          ? <Discussion sg={sg} busy={busy === "chat"} onSend={(m) => act("chat", () => api.govSuggestionChat(docId, sg.id, m), "The AI didn't reply this time.")} />
          : <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5 space-y-2 text-xs">
              {sg.thread.map((m, i) => <div key={i}><b>{m.role === "assistant" ? "AI" : m.by_name}:</b> <AiText text={m.text} sources={m.sources} ndisSources={m.ndis_sources} /></div>)}
            </div>
      )}
      {sg.status === "open" && sg.proposal && (
        <Proposal p={sg.proposal} busy={busy} locked={locked} onApply={apply} onRevise={propose}
          onCancel={() => act("cancel", () => api.govSuggestionCancel(docId, sg.id), "Couldn't cancel it.")} />
      )}
    </li>
  );
}

/** Results of the AI review of a draft. Each suggestion can be discussed with the AI,
 *  carried out (the AI proposes an edit, previewed before it's applied) or dismissed. */
export default function DocumentAiReviewPanel({ review, running, onRun, onClose, docId, locked, beforeEdit, onReview, onDraftChanged }) {
  const [open, setOpen] = useState(true);
  const [showDone, setShowDone] = useState(false);
  if (!review && !running) return null;
  const sugs = review?.suggestions || [];
  const openSugs = sugs.filter((s) => s.status === "open");
  const done = sugs.filter((s) => s.status !== "open");
  const card = (s) => (
    <SuggestionCard key={s.id} sg={s} n={sugs.indexOf(s) + 1} review={review} docId={docId} locked={locked}
      beforeEdit={beforeEdit} onReview={onReview} onDraftChanged={onDraftChanged} />
  );

  return (
    <div className="rounded-xl border bg-white mb-3 overflow-hidden" style={{ borderColor: `${ACCENT}40` }} data-testid="ai-review">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100" style={{ backgroundColor: `${ACCENT}0d` }}>
        <button className="flex items-center gap-2 text-sm font-semibold text-slate-800" onClick={() => setOpen((o) => !o)}>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Sparkles className="h-4 w-4" style={{ color: ACCENT }} /> AI review
          {review && !running && (
            <span className="text-[11px] font-normal text-slate-500">
              {openSugs.length} open{done.length ? ` · ${done.length} done or dismissed` : ""}
            </span>
          )}
        </button>
        <div className="flex items-center gap-2">
          {review && !running && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onRun} data-testid="ai-review-rerun">
              <RefreshCw className="h-3 w-3 mr-1" /> Review again
            </Button>
          )}
          <button className="p-1 text-slate-400 hover:text-slate-600" onClick={onClose} title="Hide"><X className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {open && (running ? (
        <div className="flex items-center gap-2 px-4 py-6 text-sm text-slate-500" data-testid="ai-review-running">
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: ACCENT }} />
          Reviewing the draft against the other current documents and NDIS Commission guidance. This takes up to about 30 seconds…
        </div>
      ) : (
        <div className="px-4 py-3 space-y-3">
          <div className="text-[11px] text-slate-500">
            Reviewed by AI for {review.by_name} on {fmtLog(review.at)}. Discuss a suggestion with the AI, have it make the change
            (you'll see it before it's applied), or dismiss it. Reviewing again replaces these suggestions and their discussions.
          </div>
          {review.stale && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="ai-review-stale">
              <AlertTriangle className="h-4 w-4 shrink-0" /> The draft has changed since this review. Some suggestions may already be dealt with. Use Review again for an up-to-date one.
            </div>
          )}
          {review.summary && <p className="text-sm text-slate-700">{review.summary}</p>}
          {!sugs.length ? (
            <div className="text-sm text-slate-500">No suggestions. The AI didn't find anything to improve.</div>
          ) : (
            <>
              {openSugs.length > 0
                ? <ol className="space-y-2.5" data-testid="ai-review-suggestions">{openSugs.map(card)}</ol>
                : <div className="text-sm text-slate-500">All suggestions have been dealt with.</div>}
              {done.length > 0 && (
                <div>
                  <button className="text-[11px] text-slate-500 hover:text-slate-700 flex items-center gap-1" onClick={() => setShowDone((v) => !v)} data-testid="ai-review-show-done">
                    {showDone ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    {done.length} applied or dismissed
                  </button>
                  {showDone && <ol className="space-y-2 mt-2">{done.map(card)}</ol>}
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
